import React, { useState } from 'react';
import api from '../api/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Edit, Trash2, BookOpen, FileText, Barcode, ArrowLeftRight } from 'lucide-react';
import NewCheckoutModal from '../components/checkouts/NewCheckoutModal';
import EditBookModal from '../components/books/EditBookModal';
import BarcodeModal from '../components/ui/BarcodeModal';
import FileUpload from '../components/books/FileUpload';
import { getBook, deleteBook, getBookCheckouts } from '../api/books';
import toast from 'react-hot-toast';
import { useEffect } from 'react';

const COVERS = ['var(--accent-bg)','var(--success-bg)','var(--warning-bg)','#1a0d2e','#0a1f2e','var(--danger-bg)'];
const CCOLORS = ['var(--accent)','var(--success)','var(--warning)','#a78bfa','#38bdf8','var(--danger)'];

export default function BookDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const urlBarcode = new URLSearchParams(location.search).get('barcode') || '';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');
  const [showBarcode, setShowBarcode] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [copySearch, setCopySearch] = React.useState('');
  useEffect(() => {
    if (urlBarcode) {
      setActiveTab('copies');
      setCopySearch(urlBarcode);
    }
  }, [urlBarcode]);

  const { data, isLoading } = useQuery({
    queryKey: ['book', id],
    queryFn: () => getBook(Number(id)),
  } as any);

  const { data: checkoutsData } = useQuery({
    queryKey: ['book-checkouts', id],
    queryFn: () => getBookCheckouts(Number(id)),
    enabled: activeTab === 'history',
  } as any);

  const deleteMutation = useMutation({
    mutationFn: () => deleteBook(Number(id)),
    onSuccess: () => {
      toast.success('წიგნი წაიშალა');
      qc.invalidateQueries({ queryKey: ['books'] });
      navigate('/books');
    },
    onError: () => toast.error('შეცდომა')
  });

  if (isLoading) return (
    <div style={{padding:24,color:'var(--text-faint)',fontFamily:'system-ui,sans-serif'}}>იტვირთება...</div>
  );

  const book = (data as any)?.data;
  if (!book) return null;
  const ci = (book.id || 0) % 6;
  const copies = book.copies || [];
  const available = copies.filter((c: any) => c.status === 'available').length;
  const checkouts = (checkoutsData as any)?.data?.checkouts || [];

  const tabs = [
    { key: 'info', label: 'ინფო', icon: 'ti-info-circle' },
    { key: 'copies', label: `კოპიები (${copies.length})`, icon: 'ti-books' },
    { key: 'history', label: 'ისტორია', icon: 'ti-history' },
    { key: 'file', label: 'ფაილი', icon: 'ti-file' },
  ];

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:960,margin:'0 auto'}}>
      <BarcodeModal type="book" id={book.id} open={showBarcode} onClose={() => setShowBarcode(false)} />
      <EditBookModal open={showEdit} onClose={() => setShowEdit(false)} book={book} />
      <NewCheckoutModal open={showCheckout} onClose={() => { setShowCheckout(false); qc.invalidateQueries({ queryKey: ['book', id] }); qc.invalidateQueries({ queryKey: ['stats'] }); }} preselectedBookId={book.id} />
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}>
        <button onClick={() => navigate('/books')}
          style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'var(--text-muted)',cursor:'pointer'}}>
          <ArrowLeft size={14} /> {t('catalog')}
        </button>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          {copies.filter((c: any) => c.status==='available').length > 0 && (
          <button onClick={() => setShowCheckout(true)}
            style={{display:'flex',alignItems:'center',gap:6,background:'#1d4ed8',border:'none',borderRadius:8,padding:'7px 12px',fontSize:12,color:'white',cursor:'pointer'}}>
            <ArrowLeftRight size={13} /> გაცემა
          </button>)}
          <button onClick={() => setShowBarcode(true)} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'#a78bfa',cursor:'pointer'}}>
            <i className="ti ti-barcode" style={{fontSize:14}} /> ბარკოდი
          </button>
          <button onClick={() => setShowEdit(true)} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'var(--accent)',cursor:'pointer'}}>
            <Edit size={13} /> რედაქტირება
          </button>
          <button onClick={() => { if(window.confirm('დარწმუნებული ხარ?')) deleteMutation.mutate(); }}
            style={{display:'flex',alignItems:'center',gap:6,background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:8,padding:'7px 12px',fontSize:12,color:'var(--danger)',cursor:'pointer'}}>
            <Trash2 size={13} /> წაშლა
          </button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:24,marginBottom:24}}>
        <div style={{width:120,height:160,borderRadius:12,background:COVERS[ci],color:CCOLORS[ci],display:'flex',alignItems:'center',justifyContent:'center',fontSize:48,fontWeight:700,border:`1px solid ${CCOLORS[ci]}33`,flexShrink:0}}>
          {(book.title||'?')[0]}
        </div>
        <div>
          <h1 style={{fontSize:22,fontWeight:600,color:'var(--text-primary)',margin:'0 0 6px',lineHeight:1.3}}>{book.title||'—'}</h1>
          <p style={{fontSize:14,color:'var(--text-muted)',margin:'0 0 4px'}}>{book.author||'—'}</p>
          {book.editor && <p style={{fontSize:12,color:'var(--text-faint)',margin:'0 0 2px'}}>რედ: {book.editor}</p>}
          {book.translator && <p style={{fontSize:12,color:'var(--text-faint)',margin:'0 0 16px'}}>მთარგ: {book.translator}</p>}
          {!book.editor && !book.translator && <div style={{marginBottom:16}}/>}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
            <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,background:'var(--success-bg)',color:'var(--success)',border:'1px solid #0a4a2a'}}>
              <BookOpen size={12} style={{display:'inline',marginRight:4}} />
              {available} / {copies.length} ხელმისაწვდომი
            </span>
            {book.collection_name && (
              <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,background:'var(--accent-bg)',color:'var(--accent)',border:'1px solid #1e3a6e'}}>
                {book.collection_name}
              </span>
            )}
            {book.isbn && (
              <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,background:'var(--bg-dark2)',color:'var(--text-faint)',border:'1px solid #1e2d45',fontFamily:'monospace'}}>
                ISBN: {book.isbn}
              </span>
            )}
            {book.file_path && (
              <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,background:'#1a0d2e',color:'#a78bfa',border:'1px solid #3a1d6e'}}>
                <FileText size={12} style={{display:'inline',marginRight:4}} />
                {book.file_type?.toUpperCase()}
              </span>
            )}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
            {[
              {label:'გამომცემელი', val:book.publisher||'—'},
              {label:'გამოც. ადგილი', val:book.place_of_publication||'—'},
              {label:'წელი', val:book.pub_year||'—'},
              {label:'ენა', val:book.language||'—'},
              {label:'გამოცემა', val:book.edition||'—'},
              {label:'გვერდები', val:book.pages||'—'},
              {label:'მასალის ტიპი', val:({'book':'წიგნი','periodical':'პერიოდიკა','dissertation':'დისერტაცია','dissertation_abstract':'სადის. მაცნე','cd':'CD','proceedings':'შრომები','xerox':'ქსეროასლი'} as any)[book.material_type]||book.material_type||'—'},
              {label:'სერია', val:book.series ? (book.series_number ? book.series+' №'+book.series_number : book.series) : '—'},
              {label:'ტომი', val:book.volume ? (book.volume_number ? book.volume+' / '+book.volume_number : book.volume) : '—'},
              {label:'საბუღ. ანგარიში', val:book.full_account||'—'},
              {label:'UDC', val:book.udc||'—'},
              {label:'თაროს კოდი', val:book.shelf_code||'—'},
            ].filter(f => f.val !== '—').map(f => (
              <div key={f.label} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:4}}>{f.label}</div>
                <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500}}>{f.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
        <div style={{display:'flex',borderBottom:'1px solid #1e2d45'}}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{padding:'12px 20px',background:'none',border:'none',borderBottom:activeTab===tab.key?'2px solid #60a5fa':'2px solid transparent',marginBottom:-1,fontSize:13,color:activeTab===tab.key?'var(--accent)':'var(--text-faint)',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontWeight:activeTab===tab.key?500:400}}>
              <i className={`ti ${tab.icon}`} style={{fontSize:15}} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'info' && (
          <div style={{padding:20}}>
            {book.description && <p style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.7,marginBottom:12}}>{book.description}</p>}
            {book.subject && <p style={{fontSize:12,color:'var(--text-faint)',marginBottom:6}}><span style={{color:'var(--text-dimmed)'}}>საგანი: </span>{book.subject}</p>}
            {book.notes && <p style={{fontSize:12,color:'var(--text-faint)',marginBottom:6}}><span style={{color:'var(--text-dimmed)'}}>შენიშვნა: </span>{book.notes}</p>}
            {!book.description && !book.subject && !book.notes && <p style={{fontSize:13,color:'var(--text-dimmed)',fontStyle:'italic'}}>აღწერა არ არის</p>}
          </div>
        )}

        {activeTab === 'copies' && (
          <div>
            <div style={{padding:'12px 20px 8px',position:'relative'}}>
              <input
                autoFocus
                value={copySearch}
                onChange={e => setCopySearch(e.target.value)}
                placeholder="ბარკოდით ძიება..."
                style={{width:'100%',boxSizing:'border-box',padding:'7px 32px 7px 12px',background:'#0a1628',border:'1px solid #1e2d45',borderRadius:7,color:'var(--text-secondary)',fontSize:13,fontFamily:'monospace',outline:'none'}}
              />
              {copySearch && (
                <button onClick={() => setCopySearch('')}
                  style={{position:'absolute',right:28,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-faint)',cursor:'pointer',fontSize:16,lineHeight:1,padding:2}}>
                  ✕
                </button>
              )}
            </div>
            {copies.filter((c:any) => !copySearch || c.barcode?.toLowerCase().includes(copySearch.toLowerCase())).length === 0
              ? <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>{copySearch ? 'ბარკოდი ვერ მოიძებნა' : 'კოპია არ არის'}</p>
              : copies.filter((c:any) => !copySearch || c.barcode?.toLowerCase().includes(copySearch.toLowerCase())).map((c: any) => (
                <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #0f1826',background:c.status!=='available'?'#1a0f0022':'transparent'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:c.status==='available'?'var(--success)':'var(--warning)',flexShrink:0}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,color:'var(--text-secondary)',fontFamily:'monospace'}}>{c.barcode||`#${c.id}`}</div>
                    {c.inv_number && <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>საინვ: {c.inv_number}</div>}
                    {c.status!=='available' && c.member_name && (
                      <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4}}>
                        <div style={{width:20,height:20,borderRadius:5,background:'var(--warning-bg)',border:'1px solid #4a3010',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'var(--warning)',flexShrink:0}}>
                          {c.member_name[0]}
                        </div>
                        <span style={{fontSize:12,color:'var(--warning)'}}>{c.member_name}</span>
                        {c.due_date && <span style={{fontSize:10,color:new Date(c.due_date)<new Date()?'var(--danger)':'var(--text-faint)'}}>· {new Date(c.due_date).toLocaleDateString()}</span>}
                      </div>
                    )}
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:500,
                    background:c.status==='available'?'var(--success-bg)':'var(--warning-bg)',
                    color:c.status==='available'?'var(--success)':'var(--warning)'}}>
                    {c.status==='available'?'ხელმისაწვდომი':'გაცემული'}
                  </span>
                  {c.status!=='available' && c.checkout_id && (
                    <button onClick={async () => {
                      if (!window.confirm('წიგნი დაბრუნებულად მოინიშნოს?')) return;
                      try {
                        await api.put(`/checkouts/${c.checkout_id}/return`, {});
                        window.location.reload();
                      } catch(e) { alert('შეცდომა'); }
                    }}
                    style={{padding:'4px 10px',background:'var(--success-bg)',border:'1px solid #0a4a2a',borderRadius:6,fontSize:11,color:'var(--success)',cursor:'pointer',flexShrink:0}}>
                      ↩ დაბრუნება
                    </button>
                  )}
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            {checkouts.length === 0
              ? <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>ისტორია არ არის</p>
              : checkouts.map((c: any) => (
                <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #0f1826'}}>
                  <div style={{width:32,height:32,borderRadius:8,background:'var(--bg-active)',border:'1px solid #2a3f6e',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:600,color:'var(--accent)',flexShrink:0}}>
                    {(c.member_name||'?')[0]}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500}}>{c.member_name}</div>
                    <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>
                      {new Date(c.checked_out_at).toLocaleDateString()} → {c.returned_at ? new Date(c.returned_at).toLocaleDateString() : 'ჯერ კიდევ'}
                    </div>
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:500,
                    background:c.returned_at?'var(--success-bg)':new Date(c.due_date)<new Date()?'var(--danger-bg)':'var(--warning-bg)',
                    color:c.returned_at?'var(--success)':new Date(c.due_date)<new Date()?'var(--danger)':'var(--warning)'}}>
                    {c.returned_at?'დაბრუნდა':new Date(c.due_date)<new Date()?'ვადაგადაცილებული':'აქტიური'}
                  </span>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'file' && (
          <div style={{padding:20}}>
            <FileUpload bookId={book.id} filePath={book.file_path} fileType={book.file_type} onUpdate={() => { window.location.reload(); }} />
          </div>
        )}
      </div>
    </div>
  );
}
